import { useCallback, useEffect, useState } from "react";

import { logs, type LogPage } from "../lib/api.js";
import { SEVERITY_FILTERS, lineTime, severityColor, severityLabel } from "../lib/log-view.js";
import { CONTROL_BUTTON, CONTROL_ROW } from "./control-style.js";

/**
 * The device's remote syslog, newest first.
 *
 * Fetched once and on demand, not on a timer, for the same reason the charts
 * are: a list that rewrites itself while someone is reading a line is worse
 * than one that holds still. REFRESH is how it updates.
 */
export function DeviceLogsSection({ deviceId, address }: { deviceId: string; address: string }) {
  const [severity, setSeverity] = useState<number | null>(null);
  const [draft, setDraft] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState<LogPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (before: string | null) => {
      setLoading(true);
      setError(null);
      try {
        const result = await logs.list(deviceId, { severity, q: search, before });
        // Older pages extend the list; a fresh load replaces it.
        setPage((prev) => (before && prev ? { ...result, lines: [...prev.lines, ...result.lines] } : result));
      } catch (err) {
        setError(err instanceof Error ? err.message : "Could not load logs");
      } finally {
        setLoading(false);
      }
    },
    [deviceId, severity, search],
  );

  useEffect(() => {
    void load(null);
  }, [load]);

  const filtered = severity !== null || search !== "";

  return (
    <section style={cardStyle}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 8 }}>
        <h2 style={sectionTitle}>&gt; LOGS</h2>
        <div style={{ ...CONTROL_ROW, flexWrap: "wrap" }}>
          <div style={{ display: "flex", gap: 6 }}>
            {SEVERITY_FILTERS.map((filter) => {
              const selected = filter.max === severity;
              return (
                <button
                  key={filter.label}
                  onClick={() => setSeverity(filter.max)}
                  style={{
                    ...CONTROL_BUTTON,
                    whiteSpace: "pre",
                    border: `1px solid ${selected ? "var(--text-primary)" : "var(--border)"}`,
                    background: selected ? "var(--text-primary)" : "transparent",
                    color: selected ? "var(--page-plane)" : "var(--text-secondary)",
                  }}
                >
                  {selected ? `[${filter.label}]` : ` ${filter.label} `}
                </button>
              );
            })}
          </div>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              setSearch(draft.trim());
            }}
            style={{ display: "flex", gap: 6 }}
          >
            <input
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              placeholder="search"
              maxLength={200}
              style={{
                ...CONTROL_BUTTON,
                cursor: "text",
                width: 160,
                background: "transparent",
                border: "1px solid var(--border)",
                color: "var(--text-primary)",
                fontFamily: "inherit",
              }}
            />
          </form>
          <button
            onClick={() => void load(null)}
            disabled={loading}
            style={{ ...CONTROL_BUTTON, border: "1px solid var(--border)", background: "transparent", color: "var(--text-secondary)" }}
          >
            [ REFRESH ]
          </button>
        </div>
      </div>

      {error && <p style={{ color: "var(--status-critical)", fontSize: 12, marginTop: 0 }}>{`!! ${error}`}</p>}

      {page && page.latest_at === null ? (
        <SetupHint viaConnector={page.via_connector} target={page.syslog_address} address={address} />
      ) : page && page.lines.length === 0 ? (
        <p style={{ color: "var(--text-muted)", fontSize: 13, margin: 0 }}>
          {filtered ? "NO LINES MATCH." : "NO LINES IN THE LAST 30 DAYS."}
        </p>
      ) : page ? (
        <div style={{ opacity: loading ? 0.6 : 1, transition: "opacity 150ms" }}>
          <div
            style={{
              maxHeight: 480,
              overflowY: "auto",
              border: "1px solid var(--border)",
              padding: "6px 10px",
              fontSize: 12,
              lineHeight: 1.5,
            }}
          >
            {page.lines.map((line) => (
              <div key={line.seq} style={{ display: "flex", gap: 12, alignItems: "baseline" }}>
                <span style={{ color: "var(--text-muted)", whiteSpace: "pre", flexShrink: 0 }}>{lineTime(line.t)}</span>
                <span style={{ color: severityColor(line.severity), whiteSpace: "pre", flexShrink: 0 }}>
                  {severityLabel(line.severity)}
                </span>
                {/* Wraps anywhere: a log line can be one long token -- a URL,
                    a hex dump -- and must not widen the page. */}
                <span style={{ color: "var(--text-primary)", minWidth: 0, overflowWrap: "anywhere" }}>{line.message}</span>
              </div>
            ))}
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8, fontSize: 11, color: "var(--text-muted)", letterSpacing: "0.06em" }}>
            <span>{`${page.lines.length} LINES · TIMES ARE WHEN RECEIVED`}</span>
            {page.next && (
              <button
                onClick={() => void load(page.next)}
                disabled={loading}
                style={{ ...CONTROL_BUTTON, border: "1px solid var(--border)", background: "transparent", color: "var(--text-secondary)" }}
              >
                [ OLDER ]
              </button>
            )}
          </div>
        </div>
      ) : (
        <p style={{ color: "var(--text-muted)", fontSize: 13, margin: 0 }}>LOADING...</p>
      )}
    </section>
  );
}

/**
 * Shown until the first line arrives, with what to configure.
 *
 * src-address matters more than it looks: lines are matched to a device by
 * the address they come from, and a router sends from whichever interface
 * faces this server -- which need not be the address it is polled on.
 */
function SetupHint({ viaConnector, target, address }: { viaConnector: boolean; target: string | null; address: string }) {
  if (viaConnector) {
    return (
      <p style={{ color: "var(--text-muted)", fontSize: 13, margin: 0 }}>
        Devices behind a connector can't send logs here yet.
      </p>
    );
  }
  const remote = target ?? "<this server's address>";
  return (
    <div style={{ fontSize: 13, color: "var(--text-secondary)" }}>
      <p style={{ marginTop: 0 }}>No logs received yet. On a MikroTik:</p>
      <pre
        style={{
          margin: 0,
          padding: "8px 10px",
          border: "1px solid var(--border)",
          fontSize: 12,
          whiteSpace: "pre-wrap",
          overflowWrap: "anywhere",
          color: "var(--text-primary)",
        }}
      >
        {`/system logging action add name=routerreport target=remote remote=${remote} remote-port=514 src-address=${address}\n` +
          `/system logging add topics=info action=routerreport\n` +
          `/system logging add topics=warning action=routerreport\n` +
          `/system logging add topics=error action=routerreport\n` +
          `/system logging add topics=critical action=routerreport`}
      </pre>
      <p style={{ marginBottom: 0, fontSize: 12, color: "var(--text-muted)" }}>
        {`Lines are matched by the address they come from, so they must be sent from ${address}.`}
      </p>
    </div>
  );
}

const cardStyle: React.CSSProperties = {
  background: "var(--surface-1)",
  border: "1px solid var(--border)",
  padding: 20,
  marginBottom: 16,
};

const sectionTitle: React.CSSProperties = { fontSize: 14, margin: 0, letterSpacing: "0.12em" };
